import moment from 'moment';
import isArray from 'lodash/isArray';
import reduce from 'lodash/reduce';
import { sanitizeEntity } from './sanitize';
import { ALGOLIA_SEARCH_KEY_PARAMS_HIERARCHIAL, ALGOLIA_SEARCH_KEY_PARAMS_MENU, ALGOLIA_SEARCH_KEY_PARAMS_REFINEMENT, algoliaSortOptions, algoliaUsersSortOptions, ALL_USER_TYPES, CURRENT_USER_BUSINESS_TYPE, CURRENT_USER_BUSINESS_TYPE_LABEL, CURRENT_USER_INDIVIDUAL_TYPE, CURRENT_USER_INDIVIDUAL_TYPE_LABEL, INTERCOM_EVENTS, REVIEW_TYPE_OF_PROVIDER, SEARCH_KEY_PARAMS_HIERARCHIAL, SEARCH_KEY_PARAMS_MENU, SEARCH_KEY_PARAMS_REFINEMENT, STRIPE_ONBOARDING_RETURN_URL_FAILURE, STRIPE_ONBOARDING_RETURN_URL_RESTRICTED, STRIPE_ONBOARDING_RETURN_URL_SUCCESS, USER_TYPE_RESTRICTED, USER_TYPE_UNVERIFIED, USER_TYPE_VERIFIED, UserPermissions } from './types';

import { getUrlVariants } from './cloudinary';
import { createSlug } from './urlHelpers';
import { v4 as uuidv4 } from 'uuid';
import { businessCategories } from '../config/configListing';
// NOTE: This file imports sanitize.js, which may lead to circular dependency

/**
 * Combine the given relationships objects
 *
 * See: http://jsonapi.org/format/#document-resource-object-relationships
 */
export const combinedRelationships = (oldRels, newRels) => {
  if (!oldRels && !newRels) {
    // Special case to avoid adding an empty relationships object when
    // none of the resource objects had any relationships.
    return null;
  }
  return { ...oldRels, ...newRels };
};

/**
 * Combine the given resource objects
 *
 * See: http://jsonapi.org/format/#document-resource-objects
 */
export const combinedResourceObjects = (oldRes, newRes) => {
  const { id, type } = oldRes;
  if (newRes.id.uuid !== id.uuid || newRes.type !== type) {
    throw new Error('Cannot merge resource objects with different ids or types');
  }
  const attributes = newRes.attributes || oldRes.attributes;
  const attributesOld = oldRes.attributes || {};
  const attributesNew = newRes.attributes || {};
  // Allow (potentially) sparse attributes to update only relevant fields
  const attrs = attributes ? { attributes: { ...attributesOld, ...attributesNew } } : null;
  const relationships = combinedRelationships(oldRes.relationships, newRes.relationships);
  const rels = relationships ? { relationships } : null;
  return { id, type, ...attrs, ...rels };
};

/**
 * Combine the resource objects form the given api response to the
 * existing entities.
 */
export const updatedEntities = (oldEntities, apiResponse, sanitizeConfig = {}) => {
  const { data, included = [] } = apiResponse;
  const objects = (Array.isArray(data) ? data : [data]).concat(included);

  const newEntities = objects.reduce((entities, curr) => {
    const { id, type } = curr;

    // Some entities (e.g. listing and user) might include extended data,
    // you should check if src/util/sanitize.js needs to be updated.
    const current = sanitizeEntity(curr, sanitizeConfig);

    entities[type] = entities[type] || {};
    const entity = entities[type][id.uuid];
    entities[type][id.uuid] = entity ? combinedResourceObjects({ ...entity }, current) : current;

    return entities;
  }, oldEntities);

  return newEntities;
};

/**
 * Denormalise the entities with the resources from the entities object
 *
 * This function calculates the dernormalised tree structure from the
 * normalised entities object with all the relationships joined in.
 *
 * @param {Object} entities entities object in the SDK Redux store
 * @param {Array<{ id, type }} resources array of objects
 * with id and type
 * @param {Boolean} throwIfNotFound wheather to skip a resource that
 * is not found (false), or to throw an Error (true)
 *
 * @return {Array} the given resource objects denormalised that were
 * found in the entities
 */
export const denormalisedEntities = (entities, resources, throwIfNotFound = true) => {
  const denormalised = resources.map(res => {
    const { id, type } = res;
    const entityFound = entities[type] && id && entities[type][id.uuid];
    if (!entityFound) {
      if (throwIfNotFound) {
        throw new Error(`Entity with type "${type}" and id "${id ? id.uuid : id}" not found`);
      }
      return null;
    }
    const entity = entities[type][id.uuid];
    const { relationships, ...entityData } = entity;

    if (relationships) {
      // Recursively join in all the relationship entities
      return reduce(
        relationships,
        (ent, relRef, relName) => {
          // A relationship reference can be either a single object or
          // an array of objects. We want to keep that form in the final
          // result.
          const hasMultipleRefs = Array.isArray(relRef.data);
          const multipleRefsEmpty = hasMultipleRefs && relRef.data.length === 0;
          if (!relRef.data || multipleRefsEmpty) {
            ent[relName] = hasMultipleRefs ? [] : null;
          } else {
            const refs = hasMultipleRefs ? relRef.data : [relRef.data];

            // If a relationship is not found, an Error should be thrown
            const rels = denormalisedEntities(entities, refs, true);

            ent[relName] = hasMultipleRefs ? rels : rels[0];
          }
          return ent;
        },
        entityData
      );
    }
    return entityData;
  });
  return denormalised.filter(e => !!e);
};

/**
 * Denormalise the data from the given SDK response
 *
 * @param {Object} sdkResponse response object from an SDK call
 *
 * @return {Array} entities in the response with relationships
 * denormalised from the included data
 */
export const denormalisedResponseEntities = sdkResponse => {
  const apiResponse = sdkResponse.data;
  const data = apiResponse.data;
  const resources = Array.isArray(data) ? data : [data];

  if (!data || resources.length === 0) {
    return [];
  }

  const entities = updatedEntities({}, apiResponse);
  return denormalisedEntities(entities, resources);
};

/**
 * Denormalize JSON object.
 * NOTE: Currently, this only handles denormalization of image references
 *
 * @param {JSON} data from Asset API (e.g. page asset)
 * @param {JSON} included array of asset references (currently only images supported)
 * @returns deep copy of data with images denormalized into it.
 */
const denormalizeJsonData = (data, included) => {
  let copy;

  // Handle strings, numbers, booleans, null
  if (data === null || typeof data !== 'object') {
    return data;
  }

  // At this point the data has typeof 'object' (aka Array or Object)
  // Array is the more specific case (of Object)
  if (data instanceof Array) {
    copy = data.map(datum => denormalizeJsonData(datum, included));
    return copy;
  }

  // Generic Objects
  if (data instanceof Object) {
    copy = {};
    Object.entries(data).forEach(([key, value]) => {
      // Handle denormalization of image reference
      const hasImageRefAsValue =
        typeof value == 'object' && value?._ref?.type === 'imageAsset' && value?._ref?.id;
      // If there is no image included,
      // the _ref might contain parameters for image resolver (Asset Delivery API resolves image URLs on the fly)
      const hasUnresolvedImageRef = typeof value == 'object' && value?._ref?.resolver === 'image';

      if (hasImageRefAsValue) {
        const foundRef = included.find(inc => inc.id === value._ref?.id);
        copy[key] = foundRef;
      } else if (hasUnresolvedImageRef) {
        // Don't add faulty image ref
        // Note: At the time of writing, assets can expose resolver configs,
        //       which we don't want to deal with.
      } else {
        copy[key] = denormalizeJsonData(value, included);
      }
    });
    return copy;
  }

  throw new Error("Unable to traverse data! It's not JSON.");
};

/**
 * Denormalize asset json from Asset API.
 * @param {JSON} assetJson in format: { data, included }
 * @returns deep copy of asset data with images denormalized into it.
 */
export const denormalizeAssetData = assetJson => {
  const { data, included } = assetJson || {};
  return denormalizeJsonData(data, included);
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} transaction entity object, which is to be ensured against null values
 */
export const ensureTransaction = (transaction, booking = null, listing = null, provider = null) => {
  const empty = {
    id: null,
    type: 'transaction',
    attributes: {},
    booking,
    listing,
    provider,
  };
  return { ...empty, ...transaction };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} booking entity object, which is to be ensured against null values
 */
export const ensureBooking = booking => {
  const empty = { id: null, type: 'booking', attributes: {} };
  return { ...empty, ...booking };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} listing entity object, which is to be ensured against null values
 */
export const ensureListing = listing => {
  const empty = {
    id: null,
    type: 'listing',
    attributes: { publicData: {} },
    images: [],
  };
  return { ...empty, ...listing };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} listing entity object, which is to be ensured against null values
 */
export const ensureOwnListing = listing => {
  const empty = {
    id: null,
    type: 'ownListing',
    attributes: { publicData: {} },
    images: [],
  };
  return { ...empty, ...listing };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} user entity object, which is to be ensured against null values
 */
export const ensureUser = user => {
  const empty = { id: null, type: 'user', attributes: { profile: {} } };
  return { ...empty, ...user };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} current user entity object, which is to be ensured against null values
 */
export const ensureCurrentUser = user => {
  const empty = { id: null, type: 'currentUser', attributes: { profile: {} }, profileImage: {} };
  return { ...empty, ...user };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} time slot entity object, which is to be ensured against null values
 */
export const ensureTimeSlot = timeSlot => {
  const empty = { id: null, type: 'timeSlot', attributes: {} };
  return { ...empty, ...timeSlot };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} availability exception entity object, which is to be ensured against null values
 */
export const ensureDayAvailabilityPlan = availabilityPlan => {
  const empty = { type: 'availability-plan/day', entries: [] };
  return { ...empty, ...availabilityPlan };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} availability exception entity object, which is to be ensured against null values
 */
export const ensureAvailabilityException = availabilityException => {
  const empty = { id: null, type: 'availabilityException', attributes: {} };
  return { ...empty, ...availabilityException };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} stripeCustomer entity from API, which is to be ensured against null values
 */
export const ensureStripeCustomer = stripeCustomer => {
  const empty = { id: null, type: 'stripeCustomer', attributes: {} };
  return { ...empty, ...stripeCustomer };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} stripeCustomer entity from API, which is to be ensured against null values
 */
export const ensurePaymentMethodCard = stripePaymentMethod => {
  const empty = {
    id: null,
    type: 'stripePaymentMethod',
    attributes: { type: 'stripe-payment-method/card', card: {} },
  };
  const cardPaymentMethod = { ...empty, ...stripePaymentMethod };

  if (cardPaymentMethod.attributes.type !== 'stripe-payment-method/card') {
    throw new Error(`'ensurePaymentMethodCard' got payment method with wrong type.
      'stripe-payment-method/card' was expected, received ${cardPaymentMethod.attributes.type}`);
  }

  return cardPaymentMethod;
};

/**
 * Get the display name of the given user as string. This function handles
 * missing data (e.g. when the user object is still being downloaded),
 * fully loaded users, as well as banned users.
 *
 * For banned or deleted users, a translated name should be provided.
 *
 * @param {propTypes.user} user
 * @param {String} defaultUserDisplayName
 *
 * @return {String} display name that can be rendered in the UI
 */
export const userDisplayNameAsString = (user, defaultUserDisplayName) => {
  const hasDisplayName = user?.attributes?.profile?.displayName;
  const userFirstName = user?.attributes?.profile?.firstName;
  if (hasDisplayName || userFirstName) {
    return hasDisplayName || userFirstName;
  } else {
    return defaultUserDisplayName || '';
  }
};

/**
 * DEPRECATED: Use userDisplayNameAsString function or UserDisplayName component instead
 *
 * @param {propTypes.user} user
 * @param {String} bannedUserDisplayName
 *
 * @return {String} display name that can be rendered in the UI
 */
export const userDisplayName = (user, bannedUserDisplayName) => {
  console.warn(
    `Function userDisplayName is deprecated!
User function userDisplayNameAsString or component UserDisplayName instead.`
  );

  return userDisplayNameAsString(user, bannedUserDisplayName);
};

/**
 * Get the abbreviated name of the given user. This function handles
 * missing data (e.g. when the user object is still being downloaded),
 * fully loaded users, as well as banned users.
 *
 * For banned  or deleted users, a default abbreviated name should be provided.
 *
 * @param {propTypes.user} user
 * @param {String} defaultUserAbbreviatedName
 *
 * @return {String} abbreviated name that can be rendered in the UI
 * (e.g. in Avatar initials)
 */
export const userAbbreviatedName = (user, defaultUserAbbreviatedName) => {
  const hasAttributes = user && user.attributes;
  const hasProfile = hasAttributes && user.attributes.profile;
  const hasDisplayName = hasProfile && user.attributes.profile.abbreviatedName;

  if (hasDisplayName) {
    return user.attributes.profile.abbreviatedName;
  } else {
    return defaultUserAbbreviatedName || '';
  }
};

/**
 * A customizer function to be used with the
 * mergeWith function from lodash.
 *
 * Works like merge in every way exept that on case of
 * an array the old value is completely overridden with
 * the new value.
 *
 * @param {Object} objValue Value of current field, denoted by key
 * @param {Object} srcValue New value
 * @param {String} key Key of the field currently being merged
 * @param {Object} object Target object that is receiving values from source
 * @param {Object} source Source object that is merged into object param
 * @param {Object} stack Tracks merged values
 *
 * @return {Object} New value for objValue if the original is an array,
 * otherwise undefined is returned, which results in mergeWith using the
 * standard merging function
 */
export const overrideArrays = (objValue, srcValue, key, object, source, stack) => {
  if (isArray(objValue)) {
    return srcValue;
  }
};

/**
 * Humanizes a line item code. Strips the "line-item/" namespace
 * definition from the beginnign, replaces dashes with spaces and
 * capitalizes the first character.
 *
 * @param {string} code a line item code
 *
 * @return {string} returns the line item code humanized
 */
export const humanizeLineItemCode = code => {
  if (!/^line-item\/.+/.test(code)) {
    throw new Error(`Invalid line item code: ${code}`);
  }
  const lowercase = code.replace(/^line-item\//, '').replace(/-/g, ' ');

  return lowercase.charAt(0).toUpperCase() + lowercase.slice(1);
};

// necessary params get from response
export const onGetCloudnaryData = (data) => {
  // "asset_id": "d74e145b5a5da5aa55f4f9738bc23c71",
  //   "public_id": "672c8601-3714-41f9-b75f-0c22f2935725",
  //   "version": 1730971138,
  //   "version_id": "16403bd850f7ec46f211d914a08f9a3a",
  //   "signature": "607fae955be12446c4549c265113aa7f046e91c3",
  //   "width": 341,
  //   "height": 425,
  //   "format": "png",
  //   "resource_type": "image",
  //   "created_at": "2024-11-07T09:18:58Z",
  //   "tags": [],
  //   "bytes": 177125,
  //   "type": "upload",
  //   "etag": "34da4513f8323c93a7509ebd8389ed22",
  //   "placeholder": false,
  //   "url": "http://res.cloudinary.com/turnouthq-dev/image/upload/v1730971138/672c8601-3714-41f9-b75f-0c22f2935725.png",
  //   "secure_url": "https://res.cloudinary.com/turnouthq-dev/image/upload/v1730971138/672c8601-3714-41f9-b75f-0c22f2935725.png",
  //   "asset_folder": "dev",
  //   "display_name": "edit",
  //   "access_control": [
  //       {
  //           "access_type": "anonymous"
  //       }
  //   ],
  //   "existing": false,
  //   "original_filename": "edit"
  const { width, height, format, resource_type, created_at, bytes, type, secure_url, asset_folder, display_name, original_filename, public_id } = data;
  return { width, height, format, resource_type, created_at, bytes, type, secure_url, asset_folder, display_name, original_filename, public_id }
}

// check user verified or not
export const isVerifiedBadge = (user) => user?.attributes?.profile?.publicData?.verifiedType == USER_TYPE_VERIFIED;

// Helper function to get the value from an array by key
export const getValuesFromArray = (data, key) => {
  if (!Array.isArray(data)) return ""; // Return empty string if data isn't an array
  const item = data.find((st) => st?.id === key);
  return item ? item.name : ""; // Return name if found, otherwise empty string
};

// Helper function to get the label for a given option key
export const getLabelOptionValue = (data, key) => {
  if (!Array.isArray(data)) return ""; // Return empty string if data isn't an array
  const item = data.find((st) => st?.option === key);
  return item ? item.label : ""; // Return label if found, otherwise empty string
}

// get review of author
export const onGetAuthorReviews = (reviews) => {
  const reviewsOfProvider = Array.isArray(reviews) && reviews.length ? reviews.filter(r => r?.attributes?.type === REVIEW_TYPE_OF_PROVIDER) : [];
  const reviewRating = Array.isArray(reviewsOfProvider) && reviewsOfProvider.length
    ? (reviewsOfProvider.reduce((acc, curr) => acc + (curr.attributes.rating || 0), 0)) / reviewsOfProvider.length
    : null;
  return { reviewsOfProvider, reviewRating: typeof reviewRating == "number" ? Math.round(reviewRating) : null }
};

// Get the category value based on the given category levels
export const getCategoryValue = (categories, categoryLevel1, categoryLevel2) => {
  let categoryValue = "";

  if (Array.isArray(categories) && categories.length) {
    // First, try to get categoryLevel1 value
    if (categoryLevel1) {
      categoryValue = getValuesFromArray(categories, categoryLevel1);
    }

    // If categoryLevel2 is present, check for subcategories under categoryLevel2
    if (categoryLevel2) {
      const subCategory = categories.find((st) => st.id === categoryLevel2)?.subcategories;
      if (subCategory && Array.isArray(subCategory)) {
        categoryValue = getValuesFromArray(subCategory, categoryLevel1);
      }
    }
  }

  return categoryValue; // Return 'N/A' if no value is found
};

// Get value for public data based on field configurations
export const getPublicDataValue = (listingFieldConfigs, key, value) => {
  let publicValue = "";

  // Loop through each field configuration
  if (Array.isArray(listingFieldConfigs)) {
    listingFieldConfigs.forEach((config) => {
      const { enumOptions } = config;
      // If the key matches and enumOptions are provided, find the corresponding label
      if (key === config.key && Array.isArray(enumOptions)) {
        publicValue = getLabelOptionValue(enumOptions, value);
      }
    });
  }

  return publicValue; // Default to 'N/A' if no matching value is found
};


export const getUniqueAuthors = (listings) => {
  return [...new Set(listings.filter(st => st?.author?.id?.uuid).map((st) => st.author.id.uuid))];
};

export const getExactPrice = (num) => {
  return (Math.round((+num) * 100) / 100);
};

export const getTotalPricesOfListings = (listings, deliveryMethod) => {
  const subTotal = getExactPrice(listings.reduce((acc, curr) => acc + ((curr?.attributes?.price?.amount * curr.count) || 0), 0));
  const delivery = 0;
  const total = getExactPrice(subTotal + delivery);
  const currency = listings && listings.length ? listings[0].attributes?.price?.currency : "";
  return {
    total,
    subTotal,
    delivery,
    currency
  }
};

export const isUserSelectedGroup = (user) => Array.isArray(user?.attributes?.profile?.publicData?.selectedGroups) && user?.attributes?.profile?.publicData?.selectedGroups.length ? user?.attributes?.profile?.publicData?.selectedGroups : false;


// Function to calculate the age based on selected year
export const calculateAge = (yearOfBirth) => new Date().getFullYear() - (+yearOfBirth);

// Starting year for the dropdown
export const getAllYears = (startYear = 1900) => {
  const currentYear = new Date().getFullYear() - 13; // Get the current year
  return Array.from({ length: currentYear - startYear + 1 }, (_, index) => currentYear - index); // Create an array of years
}


// get user permission based on user Role
export const getUserPermissions = (userType, type) => {
  if (type && userType && ALL_USER_TYPES.includes(userType)) {
    return UserPermissions[type] && UserPermissions[type][userType];
  }
  else {
    return null;
  }
};

// check member Ship of User
export const getUserMembershipStatus = (currentUser) => {
  return currentUser?.attributes?.profile?.publicData?.userRole;
}

// get user Role
export const currentUserRole = (currentUser) => currentUser?.attributes?.profile?.publicData?.userRole;

export const getStripeVerificationStatus = (returnURLType, attempts = 0) => {
  switch (returnURLType) {
    case STRIPE_ONBOARDING_RETURN_URL_SUCCESS:
      return USER_TYPE_VERIFIED;

    case STRIPE_ONBOARDING_RETURN_URL_FAILURE:
      return attempts >= 4 ? USER_TYPE_RESTRICTED : USER_TYPE_UNVERIFIED;

    default:
      return USER_TYPE_UNVERIFIED;
  }
};

export const truncateTextByCharacters = (text, limit) => {
  if (typeof text !== 'string' || !text.trim()) {
    return ''; // Return an empty string if the input is invalid
  }

  return text.length > limit ? text.slice(0, limit) + '...' : text;
};


export const filterNullObjectValues = (obj) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => value !== null)
  );
};


// <<<<<<<<< restrictedValidRoutes work >>>>>>>>>>

export const checkRestrictMarketPlaceEnvKeys = () => {
  const USERNAME = process.env.REACT_APP_AUTH_USERNAME;
  const PASSWORD = process.env.REACT_APP_AUTH_PASSWORD;
  const hasUsername = typeof USERNAME === 'string' && USERNAME.length > 0;
  const hasPassword = typeof PASSWORD === 'string' && PASSWORD.length > 0;
  return hasUsername && hasPassword;
};

export const onCheckRestrictMarketplcaeCred = () => {
  if (checkRestrictMarketPlaceEnvKeys()) {
    const { username = "", password = "" } = (typeof window != "undefined" && window.localStorage && window.localStorage.getItem("authCred") && typeof JSON.parse(window.localStorage.getItem("authCred")) == "object") ? JSON.parse(window.localStorage.getItem("authCred")) : {};
    return username == process.env.REACT_APP_AUTH_USERNAME && password == process.env.REACT_APP_AUTH_PASSWORD;
  }
  else {
    return true;
  }
};

export const isListingIsAFirm = (listing) => listing?.attributes?.publicData?.offers_discount == "Price is firm";

export const getMinimumPriceFirmForOffer = (listing) => {
  const { publicData, price } = listing?.attributes || {};
  const { offers_discount } = publicData || {};
  const maxPrice = price?.amount;
  return (offers_discount == "Yes: for 75% +" && maxPrice) ? maxPrice * (0.75)
    : (offers_discount == "Yes: for 50% +" && maxPrice) ? maxPrice * (0.50)
      : 100;
};

/**
 * Extracts and formats user details from the user object.
 * @param {Object} user - The user object containing user attributes and profile information.
 * @returns {Object} - A formatted object with the user's details.
 */
export const onGetUserDetails = (user) => {
  if (!user || !user.attributes || !user.attributes.profile) {
    // Return a fallback object in case the user or expected attributes are missing
    return {
      userId: null,
      name: 'Unknown User',
      email: '',
      firstName: '',
      lastName: '',
      displayName: '',
    };
  }

  const { email, profile } = user.attributes;
  const { firstName, lastName, displayName, ...rest } = profile;

  // Ensure firstName and lastName exist before concatenating
  const name = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || displayName || 'Anonymous';

  return {
    userId: user.id?.uuid || null,  // Ensure uuid is accessed safely
    name,
    email,
    firstName: firstName || '',  // Default to empty string if firstName is missing
    lastName: lastName || '',    // Default to empty string if lastName is missing
    displayName: displayName || '', // Default to empty string if displayName is missing
    ...rest, // Include any additional profile attributes
  };
};

// <<<<<<<<<<<<<<<< create and update the listing data in algolia >>>>>>>>>>
/**
 * Creates or updates the data to be sent to Algolia.
 * @param {string} indexName - The Algolia index name.
 * @param {Object} listing - The listing object containing all the details.
 * @param {Object} currentUser - The current user object.
 * @param {Object} updateParams - Optional parameters for updating the existing data.
 * @returns {Object} - The formatted data to be sent to Algolia.
 */
export const makeCreateUpdateAlgoliaData = (indexName, listing, currentUser, updateParams) => {
  const listingId = listing.id.uuid;

  // If updateParams are provided, return the update data directly
  if (updateParams) {
    return {
      indexName,
      data: {
        ...updateParams,
        objectID: listingId, // Ensure the objectID is always attached
      }
    };
  }

  // Destructure user details, adding defaults to avoid errors if any field is missing
  const {
    name = '',
    email = '',
    firstName = '',
    lastName = '',
    displayName = '',
    userId,
    publicData: uPublicData = {},
  } = onGetUserDetails(currentUser);

  const { pickupAddress, shippingAddress } = uPublicData || {};

  let seller_photo = uPublicData?.cloudProfileImage?.secure_url || "";

  // Destructure listing attributes with fallback values
  const {
    title = '',
    description = '',
    price,
    geolocation,
    state = '',
    publicData = {},
    createdAt
  } = listing.attributes || {};

  const { cloudListingImages, heroImageId, shopifyProductQuantity, source,shopifyVariants } = publicData;

  // Build pickup/shipping details from user's publicData addresses
  const normalizePlace = placeObj => {
    const sel = placeObj?.selectedPlace || {};
    const city = sel?.city || '';
    const state = sel?.selectedState || '';
    const countryCode = sel?.countryCode || '';
    const countryName = sel?.countryName || '';
    const address = sel?.address || '';
    if (!city && !countryCode && !countryName && !address) return null;
    return {
      city: city,
      state: state,
      cityState: state ? `${city}, ${state}` : city,
      address,
      CountryName: countryName,
      Countrycode: countryCode,
    };
  };

  const pickupdetails = normalizePlace(pickupAddress) || undefined;
  const shippingdetails = normalizePlace(shippingAddress) || undefined;
  const pickShippingOptions = [
    ...(pickupdetails ? ['pickup'] : []),
    ...(shippingdetails ? ['shipping'] : []),
  ];
  const pickShippingDetails = [];
  if (pickupdetails) pickShippingDetails.push({ type: 'pickup', ...pickupdetails });
  if (shippingdetails) pickShippingDetails.push({ type: 'shipping', ...shippingdetails });

  // Build additional location facets for search (city/state/country/zip/geoloc)
  const getSelPlace = addr => (addr && addr.selectedPlace) || {};
  const pickupSel = getSelPlace(pickupAddress);
  const shippingSel = getSelPlace(shippingAddress);

  const pickShippingDetailsCity = [
    pickupSel.city && pickupSel.countryCode ? `${pickupSel.city},${pickupSel.countryCode}` : null,
    shippingSel.city && shippingSel.countryCode ? `${shippingSel.city},${shippingSel.countryCode}` : null,
  ].filter(Boolean);

  const pickShippingDetailsCountry = [pickupSel.countryName, shippingSel.countryName].filter(Boolean);
  const pickShippingDetailsState = [pickupSel.selectedState, shippingSel.selectedState].filter(Boolean);
  const pickShippingDetailsZip = [pickupSel.zip, shippingSel.zip].filter(Boolean);

  const pickShippingGeoloc = [pickupSel.origin, shippingSel.origin]
    .filter(p => p && typeof p.lat === 'number' && typeof p.lng === 'number')
    .map(p => ({ lat: p.lat, lng: p.lng }));

  let image_url = "";
  if (typeof cloudListingImages == "object" && Object.keys(cloudListingImages).length) {
    image_url = heroImageId && cloudListingImages[heroImageId] ? cloudListingImages[heroImageId] : Object.values(cloudListingImages).map(st => st)[0];
  };

  // Extract stock from included array if available
  let quantity = 0;
  if (Array.isArray(listing.included)) {
    listing.included.forEach((item) => {
      if (item.type === 'stock' && item.attributes?.quantity) {
        quantity = item.attributes.quantity;
      }
    });
  }

  // Safely handle price with defaults
  const formattedPrice = price?.amount && price?.currency ? price.amount / 100 : 0;
  const currency = price?.currency || null;

  // Geolocation handling (defaults to empty object if missing or invalid)
  const geoLoc = geolocation?.lat && geolocation?.lng ? { lat: geolocation.lat, lng: geolocation.lng } : {};

  // Format the createdAt timestamp if available, default to null if missing
  const formattedCreatedAt = createdAt ? moment(createdAt).unix() : null;

  // hierarchicalCategories
  let hierarchicalCategories = {};
  const hierarchicalCatName = ["categoryName", "subCategoryName", "subSubCategoryName"];
  const allCategories = hierarchicalCatName.map((st, i) => publicData[st]).filter(st => st);
  allCategories.forEach((it, i) => hierarchicalCategories[`lvl${i}`] = allCategories.filter((st, index) => index <= i).join(" > "));

  // Build the data to be indexed in Algolia
  const data = {
    objectID: listingId, // Unique identifier for Algolia
    _geoloc: pickShippingGeoloc.length ? pickShippingGeoloc : geoLoc,  // Prefer pickup/shipping geos; fallback to listing geolocation
    title,
    id: listingId,
    description,
    state,
    price: formattedPrice,  // Price with proper formatting
    currency,
    image_url,
    publicData, // Any additional public data
    date_created: formattedCreatedAt,  // Safe conversion of createdAt
    date_updated: moment().unix(),  // Current timestamp for updatedAt
    quantity:source=="shopify" ? shopifyProductQuantity : quantity,  // Stock, if available or set to default
    allCategories,
    hierarchicalCategories,
    // seller information
    seller_id: userId,
    seller_name: displayName,
    seller_photo,
    seller_rating: 5,
    seller_first_name: firstName,
    seller_last_name: lastName,
    seller_email: email,
    isDeleted: false,
    shopifyVariants,
    ...(pickShippingDetails.length ? { pickShippingDetails } : {}),
    pickShippingOptions,
    ...(pickShippingDetailsCity.length ? { pickShippingDetailsCity } : {}),
    ...(pickShippingDetailsCountry.length ? { pickShippingDetailsCountry } : {}),
    ...(pickShippingDetailsState.length ? { pickShippingDetailsState } : {}),
    ...(pickShippingDetailsZip.length ? { pickShippingDetailsZip } : {}),
    ...(pickShippingGeoloc.length ? { pickShippingGeoloc } : {}),
  };

  return {
    indexName,
    data
  };
};

// Utility: Safely format general hours for Algolia
function formatGeneralHoursForAlgolia(hours = {}) {
  try {
    const formatted = {};
    Object.entries(hours).forEach(([day, { start, end }]) => {
      formatted[`${day}_start`] = start || "";
      formatted[`${day}_end`] = end || "";
    });

    const openDays = Object.keys(hours);
    const allDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const closedDays = allDays.filter(day => !openDays.includes(day));

    return { ...formatted, openDays, closedDays };
  } catch (err) {
    console.error("Error formatting general hours for Algolia:", err);
    return {};
  }
}

// Utility: Generate hierarchical categories for Algolia
function generateHierarchicalCategories(otherBusinessCategories = []) {
  try {
    if (!Array.isArray(otherBusinessCategories)) return [];
    return otherBusinessCategories
      .filter(item => item && item.category && item.childCategory)
      .map(item => {
        // Find the parent category in businessCategories config
        const parentCategory = businessCategories.find(cat => cat.key === item.category || cat.label === item.category);
        // Get the child category label
        let childCategoryLabel = item.childCategory;
        if (parentCategory && parentCategory.childCategories) {
          const childCategory = parentCategory.childCategories.find(
            child => child.key === item.childCategory
          );
          if (childCategory) {
            childCategoryLabel = childCategory.label;
          }
        }

        // Use the parent category label (item.category is already the label)
        const parentCategoryLabel = item.category;

        return {
          lvl0: parentCategoryLabel,
          lvl1: `${parentCategoryLabel} > ${childCategoryLabel}`,
        };
      });
  } catch (err) {
    console.error("Error generating hierarchical categories:", err);
    return [];
  }
}

// ✅ Main transformer
function transformUserToAlgoliaRecord(user) {
  try {
    if (!user || typeof user !== "object") throw new Error("Invalid user object");
    if (!user.id?.uuid) throw new Error("Missing user ID");
    if (!user.attributes) throw new Error("Missing user attributes");

    const { attributes } = user;
    const profile = attributes.profile || {};
    const publicData = profile.publicData || {};
    const privateData = profile.privateData || {};

    const createdAt = attributes.createdAt ? moment(attributes.createdAt).unix() : null;

    // Extract location data (shipping + pickup formatted)
    const locationData = makeAlogoliaLocationData({
      userId: user.id.uuid,
      shippingAddress: publicData.shippingAddress,
      pickupAddress: publicData.pickupAddress,
    });

    const primaryCategory = publicData.primaryBusinessCategory?.label || "";
    const otherBusinessCategories = Array.isArray(publicData.otherBusinessCategories)
      ? publicData.otherBusinessCategories
      : [];

    // Generate hierarchical categories only from otherBusinessCategories
    const hierarchicalCategories = generateHierarchicalCategories(otherBusinessCategories);

    // Extract unique lvl0 values for mobile filtering
    const categoryLevels = [...new Set(hierarchicalCategories.map(cat => cat.lvl0).filter(Boolean))];

    const businessOrigin = publicData.businessAddress?.selectedPlace?.origin;

    return {
      // 🔑 Identifiers
      objectID: user.id.uuid,
      userId: user.id.uuid,

      // 👤 Profile
      displayName: profile.displayName || "",
      firstName: profile.firstName || "",
      lastName: profile.lastName || "",
      abbreviatedName: profile.abbreviatedName || "",
      bio: profile.bio || "",
      dob: publicData.dob || "",
      phoneNumber: publicData.phoneNumber || "",
      state: attributes.state || "inactive",
      currentUserType: publicData.currentUserType == CURRENT_USER_BUSINESS_TYPE ? CURRENT_USER_BUSINESS_TYPE_LABEL
        : publicData.currentUserType == CURRENT_USER_INDIVIDUAL_TYPE ? CURRENT_USER_INDIVIDUAL_TYPE_LABEL
          : CURRENT_USER_INDIVIDUAL_TYPE_LABEL,
      userRole: publicData.userRole || "basic",

      // 🏢 Business
      storeName: publicData.storeName || "",
      customBusinessCategory: publicData.customBusinessCategory || "",
      customOtherBusinessCategory: publicData.customOtherBusinessCategory || "",
      primaryBusinessCategory: primaryCategory,
      otherBusinessCategories,
      hierarchicalCategories,
      categoryLevels,

      // 🐴 Associations
      horseAssociations: (publicData.horseAssociations || []).map(a => a.label),
      horseBreeds: (publicData.horseBreeds || []).map(b => b.label),
      interests: (publicData.interests || []).map(i => i.label),
      preferredCarrier: (publicData.preferredCarrier || []).map(c => c.label),
      preferredCurrier: publicData.preferredCurrier || "",

      // 📍 Location Data (merged)
      ...locationData, // includes pickup + shipping + _geoloc(we neglect from bussiness _geloc)

      // 📍 Location Data (for geo & filters)
      businessAddress: normalizePlace(publicData.businessAddress),

      _geoloc: businessOrigin && typeof businessOrigin.lat === "number" && typeof businessOrigin.lng === "number" ? { lat: businessOrigin.lat, lng: businessOrigin.lng } : null,

      // 🕒 Hours
      generalHours: formatGeneralHoursForAlgolia(publicData.generalHours || {}),

      // 📸 Media
      profileImage: publicData.cloudProfileImage?.secure_url || "",
      galleryImages: (publicData.galleryImages || []).map(img => img.secure_url),

      // 🌐 Social
      facebookUrl: publicData.facebookUrl || "",
      instagramUrl: publicData.instagramUrl || "",
      tiktokUrl: publicData.tiktokUrl || "",
      pinterestUrl: publicData.pinterestUrl || "",
      linkedinUrl: publicData.linkedinUrl || "",
      xUrl: publicData.xUrl || "",

      // 💳 Stripe + Permissions
      // stripeConnected: !!attributes.stripeConnected,
      stripePayoutsEnabled: !!attributes.stripePayoutsEnabled,
      stripeChargesEnabled: !!attributes.stripeChargesEnabled,
      elephantScore: privateData.elephantScore ?? null,
      elephantDecision: privateData.elephantDecision ?? null,
      platformPermission: privateData.platformPermission || "restricted",

      // 🕓 Meta
      createdAt,
      emailVerified: !!attributes.emailVerified,
      banned: !!attributes.banned,
      deleted: !!attributes.deleted,
    };
  } catch (err) {
    console.error("Error transforming user to Algolia record:", err.message, err.stack);
    return null;
  }
}


export const makeCreateUpdateAlgoliaUserData = (indexName, currentUser, signupParams = {}, updateParams) => {
  const userId = currentUser?.id?.uuid;

  // Always transform the full user data
  const userData = transformUserToAlgoliaRecord(currentUser);

  // If updateParams are provided, merge them with the full user data
  if (updateParams) {
    return {
      indexName,
      data: {
        ...userData,
        ...updateParams,
        objectID: userId, // Ensure the objectID is always attached
      }
    };
  }

  return {
    indexName,
    data: userData
  };
};




// {
//   "RITA_DEV": {
//       "configure": {
//           "filters": "state:\"published\" AND (publicData.totalStockAvailable > 0)",
//           "ruleContexts": [],
//           "hitsPerPage": 12
//       },
//       "query": "bus",
//       "menu": {
//           "publicData.primaryCategory": "Pēpi",
//           "publicData.subCategory": "Wear"
//       },
//       "sortBy": "RITA_DEV_price_asc",
//       "range": {
//           "price": "1:15"
//       }
//   }
// }




export const makeAlgoliaSearchParams = (indexName, value) => {
  const searchParams = {};
  const { menu, query, sortBy, range, page, refinementList, hierarchicalMenu } = typeof value == "object" ? value[indexName] : {};

  // search query
  query && Object.assign(searchParams, { query: query });

  // page query
  page && Object.assign(searchParams, { page: page });

  // sortBy
  sortBy && [...algoliaSortOptions, ...algoliaUsersSortOptions].filter((st) => st.value == sortBy).length
    && Object.assign(searchParams, { sort: [...algoliaSortOptions, ...algoliaUsersSortOptions].filter((st) => st.value == sortBy)[0].searchKeyName });

  // price price "10:11"
  typeof range == "object" && range.price && range.price.includes(":")
    && Object.assign(searchParams, { price: range.price.split(":")[0] + "-" + range.price.split(":")[1] });

  // refinementList
  // NOTE: Some facet values (e.g., City,CC) contain commas. For those, we must not join with ",".
  // Use a safe separator ("|") for pickShippingDetails.city → URL param key: pickShippingDetailsCity
  typeof refinementList == "object" && Object.keys(refinementList).length && Object.keys(refinementList).map((st) => {
    const uiParamKey = SEARCH_KEY_PARAMS_REFINEMENT[st];
    if (!uiParamKey || !Array.isArray(refinementList[st])) return;
    const isCityFacet = st === 'pickShippingDetails.city';
    const joined = isCityFacet ? refinementList[st].join('|') : refinementList[st].join(',');
    Object.assign(searchParams, { [uiParamKey]: joined });
  });

  // SEARCH_KEY_PARAMS_HIERARCHIAL
  typeof hierarchicalMenu == "object" && Object.keys(hierarchicalMenu).length && Object.keys(hierarchicalMenu).map((st) => SEARCH_KEY_PARAMS_HIERARCHIAL[st] && Array.isArray(hierarchicalMenu[st]) && Object.assign(searchParams, {
    [SEARCH_KEY_PARAMS_HIERARCHIAL[st]]: hierarchicalMenu[st].join(",")
  }));

  // menu filters
  typeof menu == "object" && Object.keys(menu).length && Object.keys(menu).map((st) => SEARCH_KEY_PARAMS_MENU[st] && Object.assign(searchParams, {
    [SEARCH_KEY_PARAMS_MENU[st]]: menu[st]
  }));

  return searchParams;
};

export const setAlgoliaInitailState = (indexName, value, configureProps = false, isSearchPage) => {
  const initialState = {
    [indexName]: {
      "configure": configureProps ? configureProps : {
        // "filters": "state:\"published\" AND (quantity > 0)",
        "ruleContexts": [],
        "hitsPerPage": 12
      },
      refinementList: {

      },
      menu: {

      },
      toggle: {

      },
      hierarchicalMenu: {

      }
    }
  };

  const {
    sort,
    price,
    query,
    page,
    ...rest
  } = typeof value == "object" ? value : {};

  // search query
  query && Object.assign(initialState[indexName], { query });

  // search query
  page && Object.assign(initialState[indexName], { page });

  // sortBy
  sort && [...algoliaSortOptions, ...algoliaUsersSortOptions].filter((st) => st.searchKeyName == sort).length
    && Object.assign(initialState[indexName], { sortBy: [...algoliaSortOptions, ...algoliaUsersSortOptions].filter((st) => st.searchKeyName == sort)[0].value });

  // price
  const newPrice = price ? price + "" : "";
  newPrice && Object.assign(initialState[indexName], { range: { price: newPrice.includes("-") ? newPrice.split("-")[0] + ":" + newPrice.split("-")[1] : newPrice } });

  // all refinementList
  // Decode pickShippingDetailsCity using '|' separator when present.
  // If legacy URLs used ',' as a separator, regroup tokens into City,CC pairs.
  isSearchPage && typeof rest == "object" && Object.keys(rest).length
    && Object.keys(rest).map((st) => {
      const attrKey = ALGOLIA_SEARCH_KEY_PARAMS_REFINEMENT[st];
      if (!attrKey) return;
      const rawVal = String(rest[st] ?? '');
      let values;
      if (st === 'pickShippingDetailsCity') {
        if (rawVal.includes('|')) {
          values = rawVal.split('|');
        } else if (rawVal.includes(',')) {
          // Regroup comma-separated tokens into pairs: [City,CC, City2,CC2] -> ["City,CC","City2,CC2"]
          const parts = rawVal.split(',').map(s => s.trim()).filter(Boolean);
          const pairs = [];
          for (let i = 0; i < parts.length; i += 2) {
            if (parts[i] && parts[i + 1]) pairs.push(`${parts[i]},${parts[i + 1]}`);
          }
          values = pairs;
        } else {
          values = rawVal ? [rawVal] : [];
        }
      } else {
        values = rawVal.split(',');
      }
      Object.assign(initialState[indexName].refinementList, { [attrKey]: values.filter(Boolean) });
    });

  // alll refinemnetList
  isSearchPage && typeof rest == "object" && Object.keys(rest).length
    && Object.keys(rest).map((st) => ALGOLIA_SEARCH_KEY_PARAMS_HIERARCHIAL[st] && Object.assign(initialState[indexName].hierarchicalMenu, { [ALGOLIA_SEARCH_KEY_PARAMS_HIERARCHIAL[st]]: String(rest[st]).split(",") }));

  // ALGOLIA_SEARCH_KEY_PARAMS_MENU
  typeof rest == "object" && Object.keys(rest).length
    && Object.keys(rest).map((st) => ALGOLIA_SEARCH_KEY_PARAMS_MENU[st] && Object.assign(initialState[indexName].menu, { [ALGOLIA_SEARCH_KEY_PARAMS_MENU[st]]: rest[st] }));


  return initialState;
}

export const getShippingDetails = (currentUser, pageData, values = {}) => {
  const { shippingDimensions, shippingHeight, shippingLength, shippingWeight, shippingWidth, shippingWeightType } = pageData?.listing?.attributes?.publicData || {};

  const shippingWeightIs = shippingWeight && shippingWeightType == "oz" ? parseFloat((+shippingWeight) / 16) : shippingWeight;

  const dimensions = shippingDimensions.match(/(\d+)"\s*x\s*(\d+)"/) || shippingDimensions.match(/(\d+)'\s*x\s*(\d+)'\s*x\s*(\d+)'/) ||
    [];

  const buyerShippingAddress = values.deliveryAddress ? getShippoAddresFromValues(currentUser, values.deliveryAddress) : getShippoAddress(currentUser, "deliveryAddress")

  const cartItems = pageData?.cartListings || [];

  const cartItemsWeight = cartItems?.reduce((sum, item) => {
    const { shippingWeight, shippingWeightType } = item.attributes.publicData || {};
    return sum + (shippingWeight && shippingWeightType == "oz" ? parseFloat((+shippingWeight) / 16) : shippingWeight ? parseFloat(shippingWeight) : 0);
  }, 0);

  const totalWeight = cartItems && cartItems.length > 0 ? cartItemsWeight : shippingWeightIs;

  // const cartListingDimensions = cartItems.map((item) => {
  //   const { shippingDimensions, shippingLength, shippingWidth, shippingHeight, shippingWeight } = item.attributes.publicData || {};

  //   // Extract dimensions using regex
  //   const dimensions = shippingDimensions ? shippingDimensions.match(/(\d+)"\s*x\s*(\d+)"/) || [] : [];

  //   return {
  //     distance_unit: "in",
  //     mass_unit: "lb",
  //     length: dimensions[1] ? dimensions[1] : shippingLength || 1,
  //     width: dimensions[2] ? dimensions[2] : shippingWidth || 1,
  //     height: dimensions[3] ? dimensions[3] : shippingHeight || 1,
  //     weight: shippingWeight ? shippingWeight : 0,
  //   };
  // });

  const shippoShippingDetails = {
    authorId: pageData?.listing?.author?.id?.uuid,
    buyerShippingAddress,
    listingDimensions: (shippingDimensions && dimensions.length > 0) ?
      { length: dimensions[1], width: dimensions[2], height: dimensions[3] || 1, weight: totalWeight ? totalWeight : 0 }
      : { length: shippingLength, width: shippingWidth, height: shippingHeight || 1, weight: totalWeight }
  };

  return shippoShippingDetails;
};

const getShippoStreet1 = selectedPlace => {
  const { address, streetNumber, streetName, subpremise } = selectedPlace || {};
  const streetFromComponents = streetNumber && streetName
    ? `${streetNumber} ${streetName}${subpremise ? ` ${subpremise}` : ''}`
    : null;
  const firstAddressLine = address ? address.split(',')[0] : null;
  const street1 = streetFromComponents || firstAddressLine;

  return street1 && street1.length > 48 ? street1.slice(0, 48) : street1;
};

export const getShippoAddresFromValues = (user, Address) => {
  const { profile, email } = user?.attributes || {};
  const { displayName, protectedData, firstName, lastName } = profile || {};
  const { phoneNumber } = protectedData || {};
  const { selectedPlace } = Address || {};
  const { city, selectedState, zip, countryCode } = selectedPlace || {};
  return {
    name: `${firstName} ${lastName}`,
    street1: getShippoStreet1(selectedPlace),
    city,
    state: selectedState,
    zip,
    country: countryCode,
    phone: phoneNumber,
    email,
  };
}

// key==shippingAddress
export const getShippoAddress = (user, key) => {
  const { profile, email } = user?.attributes || {};
  const { displayName, publicData = {}, protectedData, firstName, lastName } = profile || {};
  const { phoneNumber } = protectedData || {};
  const { selectedPlace } = publicData?.[key] || {};
  const { city, selectedState, zip, countryCode } = selectedPlace || {};
  return {
    name: `${firstName} ${lastName}`,
    street1: getShippoStreet1(selectedPlace),
    city,
    state: selectedState,
    zip,
    country: countryCode || publicData.countryCode || "US", // Adjust if needed
    phone: phoneNumber,
    email,
  };
}

export const isValidShippoAdress = (user, key) => {
  const { name, street1, city, state, zip, country } = getShippoAddress(user, key);
  return name && street1 && city && state && zip && country;
};

export const cartItemsCount = (cartData) => {
  let totalQuantity = 0;
  try {
    for (const cartId in cartData) {
      const items = cartData[cartId];
      for (const productId in items) {
        if (items[productId]?.count) {
          totalQuantity += 1;
          // totalQuantity += items[productId].count;
        }
      }
    };
    return totalQuantity;
  } catch (error) {
    return totalQuantity;
  }
};

export const convertImageURLwebp = (imageUrl) => {
  try {
    return imageUrl.replace(/\.(jpg|jpeg|png)$/i, '.webp');
  } catch (e) {
    return imageUrl;
  }
};

export const convertCardImageURLwebp = (url) => {
  if (!url) return "";
  return url.replace("/upload/", "/upload/w_800,h_533,c_fit,f_auto,q_auto/").replace(/\.(jpg|jpeg|png)$/i, '.webp');
};

export const getSortedListingImages = (publicData) => {
  try {
    const { cloudListingImages: cloudnaryImages = {}, heroImageId } = publicData || {};
    // Convert object to array and sort based on heroImageId
    const sortedEntries = Object.entries(cloudnaryImages).sort(([keyA], [keyB]) => {
      if (keyA === heroImageId) return -1; // Place heroImageId first
      if (keyB === heroImageId) return 1;
      return keyA.localeCompare(keyB); // Sort remaining keys in ascending order
    });
    // Convert back to object
    const sortedImages = Object.fromEntries(sortedEntries);
    return Object.values(sortedImages).map(getUrlVariants).filter(st => st);
  } catch (e) {
    console.log(e, '&&& &&& => e');
    return [];
  }
};

export const getListingCatSubCatRouteParams = (listing) => {
  try {
    const id = listing?.id?.uuid || "1111";
    const { title, state, publicData } = listing?.attributes || {};
    const {
      subCategoryName,
      categoryName
    } = publicData || {};
    return {
      id,
      slug: title ? createSlug(title) : "title",
      category: categoryName ? createSlug(categoryName) : "category",
      subCategory: subCategoryName ? createSlug(subCategoryName) : "sub-category"
    }
  } catch (error) {
    console.log(error, '&&& &&& => error');
    return {
      id: "1111",
      slug: "none",
      category: "category",
      subCategory: "sub-category"
    }
  }
};

export const getPickUpAddressOfUser = (user) => {
  const { city = "" } = user?.attributes?.profile?.publicData?.pickupAddress?.selectedPlace || {};
  return {
    city
  };
};

export const capitalizeWords = (str = '') => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};
// Starting year from 13 for the dropdown
export const getYearsFrom13 = (minAge, maxAge) => {
  const currentYear = new Date().getFullYear();

  return Array.from(
    { length: maxAge - minAge + 1 },
    (_, i) => currentYear - minAge - i
  );
};

export const makeFreeTransactionData = (response, currentUser) => {
  const {
    data,
    included
  } = response.data;

  const {
    processName,
    payoutTotal,
    processVersion,
    protectedData,
    lastTransition,
    payinTotal
  } = data.attributes;

  const {
    deliveryMethod,
    stripePaymentIntentId
  } = protectedData

  const provider = included.find((st) => st.type == "user");
  const listing = included.find((st) => st.type == "listing");

  const {
    firstName,
    lastName
  } = currentUser?.attributes?.profile || {};

  return {
    tx_id: data.id.uuid,
    tx_status: lastTransition,
    tx_process_name: processName,
    tx_process_version: processVersion,
    tx_amount: payinTotal.amount,
    tx_currency: payinTotal.currency,
    tx_delivery_type: deliveryMethod,
    tx_provider_amount: payoutTotal.amount,
    provider_id: provider.id.uuid,
    provider_name: provider?.attributes?.profile?.displayName || "",
    customer_id: currentUser.id.uuid,
    customer_name: `${firstName} ${lastName}`,
    listing_id: listing.id.uuid,
    listing_title: listing.attributes.title,
    payment_intent_id: stripePaymentIntentId,
    tx_settled_outside: "system",
    provider_transfer_status: "system",
  };
};

// make sure to add the user data
export const onMakeYouthData = (values, user) => {
  const { youth_id, youth_dob, youth_email, youth_status, youth_checks, invite_expire_time } = values || {};
  const { id, attributes } = user || {};
  const { email, profile } = attributes || {};

  return {
    youth_id, // string
    youth_dob, // string
    youth_email, // string required
    youth_status, // string required
    youth_checks, // array of strings
    guardian_email: email, // string required
    guardian_id: id.uuid, // string required
    guardian_name: profile?.firstName, // string required
    invite_id: uuidv4(), // string required
    invite_expire_time // number required
  };
};

//
export const weightOfCarries = [
  { country: "Canada Post", weight: 66 },
  { country: "Chronopost", weight: 66 },
  { country: "Colissimot", weight: 66 },
  { country: "Colissimo", weight: 66 },
  { country: "CouriersPlease", weight: 55 },
  { country: "DHL Express", weight: 70 },
  { country: "DPD DE", weight: 69 },
  { country: "DPD UK", weight: 66 },
  { country: "Hermes UK", weight: 33 },
  { country: "Sendle", weight: 44 },
  { country: "UPS", weight: 70 },
  { country: "UPS (USA)", weight: 70 },
  { country: "USPS", weight: 70 },
  { country: "LSO", weight: 20 },
  { country: "Deutsche Post", weight: 20 },
];

export const getMinimumCarrierWeight = (selectedCarriers = []) => {
  const selectedLabels = selectedCarriers?.map(carrier => carrier?.label);

  const weights = weightOfCarries
    .filter(entry => selectedLabels?.includes(entry?.country))
    .map(entry => entry.weight);

  if (weights.length === 0) return null;

  return Math.min(...weights);
};

/**
 * Constructs Intercom event payload based on user and event type
 *
 * @param {Object} user - The user object from backend or context
 * @param {string} eventType - One of the INTERCOM_EVENTS values
 * @returns {Object} - Intercom-compatible event payload
 */
export const makeIntercomEventData = (user, eventType) => {
  if (!user || !eventType) return {};

  const { id, attributes } = user;
  const { email, profile = {} } = attributes || {};
  const { publicData = {} } = profile;
  const userId = id?.uuid;

  const {
    countryCode,
    currentUserType,
    dob,
    userRole,
    zipCode
  } = publicData;

  const basePayload = {
    event_name: eventType,
    created_at: moment().unix(),
    user_id: userId,
    email: email,
    metadata: {
      countryCode,
      currentUserType,
      dob,
      userRole,
      zipCode
    },
  };

  switch (eventType) {
    case INTERCOM_EVENTS.USER_SIGNED_UP:
      return { eventData: basePayload };

    // Example: Add more event types here
    // case INTERCOM_EVENTS.PAGE_VIEWED:
    //   return {
    //     ...basePayload,
    //     metadata: {
    //       ...basePayload.metadata,
    //       page: window.location.pathname,
    //     }
    //   };

    default:
      console.warn('⚠️ Unknown Intercom event type:', eventType);
      return {};
  }
};

export const getCommunityUserRole = (moderatorGroups, groupId) => {
  if (Array.isArray(moderatorGroups) && moderatorGroups.length > 0) {
    const group = moderatorGroups.find(group => group.groupId == groupId);
    return (group?.status == "active" && group?.role) || null;
  }
  return null;
};



export const makeStripeMetaDataParams = (rawMetadata) => {
  const metadata = {};

  const MAX_KEYS = 50;
  const MAX_KEY_LENGTH = 40;
  const MAX_VALUE_LENGTH = 500;

  const entries = Object.entries(rawMetadata);

  if (entries.length > MAX_KEYS) {
    throw new Error(`Stripe metadata exceeds ${MAX_KEYS} entries.`);
  }

  for (const [key, value] of entries) {
    const stringValue = String(value || '');
    if (key.length > MAX_KEY_LENGTH) {
      throw new Error(`Metadata key '${key}' exceeds ${MAX_KEY_LENGTH} characters.`);
    }

    if (stringValue.length > MAX_VALUE_LENGTH) {
      throw new Error(`Metadata value for key '${key}' exceeds ${MAX_VALUE_LENGTH} characters.`);
    }

    metadata[key] = stringValue;
  }
  return metadata;
}

export const makeGroupPaymentData = (
  currentUser,
  {
    groupPaymentData,
    customerGroupPaymentId,
    price = 0,
    stripeSubscriptionId = "",
    currentPeriodEnd = 0,
    stripeStatus = "",
    stripeInvoiceId = "",
    invoiceStatus = "",
    invoiceAmountPaid = 0,
    invoiceCurrency = "",
    groupAdminId = "",
    groupAdminPricePay = 0,
    platformCommision = 0
  }) => {

  const userId = currentUser?.id?.uuid;
  const { email, profile } = currentUser.attributes;
  const { firstName, lastName } = profile;

  const {
    paymentName,
    paymentId,
    currency,
    groupId,
    groupName,
    paymentDescription,
    chargeType,
    type
  } = groupPaymentData;

  const metadata = makeStripeMetaDataParams({
    userFirstName: firstName,
    userLastName: lastName,
    userEmail: email,
    paymentName,
    paymentId,
    price, // // number required
    currency, // // string required
    groupId,
    groupName,
    userId,
    stripeSubscriptionId,
    customerGroupPaymentId,
    groupAdminId,
    groupAdminPricePay, // number required
    platformCommision, // number required
  });

  const customerGroupPaymentInvoiceData = {
    userFirstName: firstName, // string
    userEmail: email, // string
    userLastName: lastName, // string
    paymentName, // string >> required
    paymentId, // string >> required
    price, // number
    currency, // string >>
    groupId, // string >> required
    groupName, // string >> required
    userId, // string >> required
    stripeSubscriptionId, // string >>
    customerGroupPaymentId, // string >  required
    invoiceStatus, // string > required >> draft, open, paid, uncollectible, or void
    stripeInvoiceId, // string >  required
    invoiceAmountPaid, // number
    invoiceCurrency, // string
    groupAdminId, // string > required
    groupAdminPricePay, // number required
    platformCommision, // number required
  }

  const customerGroupPaymentData = {
    userFirstName: firstName,
    userEmail: email,
    userLastName: lastName,
    paymentName, // required
    paymentDescription,
    paymentId, // required
    chargeType, //  enum: ['Flat Fee', 'Variable Fee']
    price, // required
    currency, // required
    type, // enum: ['one-time', 'monthly']
    groupId, // required
    groupName, // required
    userId, // required
    status: stripeSubscriptionId ? "recurring" : "completed", // enum: ['completed', 'recurring'], default: 'completed'
    stripeSubscriptionId,
    currentPeriodEnd, // Unix timestamp
    stripeStatus,
    customerGroupPaymentId, // required
    groupAdminId, // required
    groupAdminPricePay, // number required
    platformCommision, // number required
  };

  return {
    customerGroupPaymentData,
    customerGroupPaymentInvoiceData,
    metadata
  }
};


export const calculateStripePlatformFee = ({
  totalAmountCents,
  platformPercent = 10, // Platform takes 10% of what's left after Stripe fees
  stripePercent = 2.9,
  stripeFixedFeeCents = 30,
}) => {
  // Step 1: Calculate Stripe fee
  const stripeFee = Math.round((totalAmountCents * (stripePercent / 100)) + stripeFixedFeeCents);

  // Step 2: Net amount after Stripe fee
  const netAfterStripe = totalAmountCents - stripeFee;


  // Step 3: Platform fee from remaining amount
  const platformFee = Math.round(netAfterStripe * (platformPercent / 100));


  // Step 4: Stripe fee as a percentage of total amount
  const application_fee_percent = parseFloat(((stripeFee / totalAmountCents) * 100).toFixed(2));


  return {
    stripeFee,            // Fee Stripe takes
    netAfterStripe,       // Amount left after Stripe fee
    platformFee,          // What the platform keeps
    transferToAdmin: netAfterStripe - platformFee, // What goes to the admin
    application_fee_percent: application_fee_percent
  };
}

export const isValidUUIDv4 = (uuid) => {
  const uuidV4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidV4Regex.test(uuid);
};

export const transformCloudinaryUrl = (url, newTransform = 'w_500,h_500,c_fit,f_auto,q_auto') => {
  try {
    return url.replace(/upload\/[^/]+/, `upload/${newTransform}`);
  } catch (error) {
    return "";
  }
};

export const checkElepantResponseSubmited = (user) => {
  const {
    reevaluationElephantScore,
    reevaluationElephantDecision
  } = user?.attributes?.profile?.privateData || {};

  if (reevaluationElephantScore && reevaluationElephantDecision) {
    return true;
  }
  return false
};

export const checkElephantModalOpen = (user) => {
  const {
    elephantDecision,
    platformPermission
  } = user?.attributes?.profile?.privateData || {};

  if (["pending", "denied"].includes(platformPermission)) {
    return true;
  }

  // if (elephantDecision && ["decline", "review"].includes(elephantDecision)) {
  //   return true;
  // }
  return false;
};


export const getLastThreeRecentChats = (chatDetails = []) => {
  if (!Array.isArray(chatDetails) || chatDetails.length === 0) return [];

  const latestByRulebook = chatDetails.reduce((acc, chat) => {
    const rulebookId = chat.rulebook_id;
    if (!rulebookId || !chat.created_at) return acc;

    // Keep only the latest chat per rulebook
    if (
      !acc[rulebookId] ||
      new Date(chat.created_at) > new Date(acc[rulebookId].created_at)
    ) {
      acc[rulebookId] = chat;
    }

    return acc;
  }, {});

  return Object.values(latestByRulebook)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 3);
};

export const getFavoriteRuleBooks = (userFavChatDetails) => {
  try {
    const { favGpt = [], gpts = [] } = userFavChatDetails || {};

    // Ensure both are arrays
    const favoriteList = Array.isArray(favGpt) ? favGpt : [];
    const allChats = Array.isArray(gpts) ? gpts : [];

    // Extract valid rulebook IDs from favorites
    const favFileIds = favoriteList
      .map(chat => chat?.rulebook_id)
      .filter(Boolean); // remove null/undefined/empty

    // Filter gpts to only those in favorites
    const favChats = allChats.filter(chat =>
      favFileIds.includes(chat?.rulebook_id)
    );

    return favChats;
  } catch (error) {
    console.error("Error getting favorite chats:", error);
    return [];
  }
};


// get Usertype
export const getUserType = (user) => {
  return user?.attributes?.profile?.publicData?.currentUserType || null;
}

const normalizePlace = placeObj => {
  const sel = placeObj?.selectedPlace || {};
  const city = sel?.city || '';
  const countryCode = sel?.countryCode || '';
  const countryName = sel?.countryName || '';
  const address = sel?.address || '';
  if (!city && !countryCode && !countryName && !address) return null;
  return {
    // city concatenated with countryCode, no delimiter per spec
    city: `${city},${countryCode}`,
    address,
    // exact casing per spec
    CountryName: countryName,
    Countrycode: countryCode,
    countryCode: countryCode,
    countryName: countryName,
    selectedState: sel?.selectedState,
    zip: sel?.zip,
    origin: {
      "lat": sel?.origin?.lat,
      "lng": sel?.origin?.lng,
    }
  };
};

export const makeAlogoliaLocationData = ({ userId, shippingAddress, pickupAddress }) => {
  try {


    const pickup = normalizePlace(pickupAddress) || undefined;
    const shipping = normalizePlace(shippingAddress) || undefined;

    // Raw selected places for facet arrays / geoloc
    const pickupSel = pickupAddress?.selectedPlace || {};
    const shippingSel = shippingAddress?.selectedPlace || {};

    // Build options list based on available details
    const pickShippingOptions = [];
    if (pickup) pickShippingOptions.push('pickup');
    if (shipping) pickShippingOptions.push('shipping');

    // Build unified details array
    const pickShippingDetails = [];
    if (pickup) pickShippingDetails.push({ type: 'pickup', ...pickup });
    if (shipping) pickShippingDetails.push({ type: 'shipping', ...shipping });

    // Build facet arrays mirroring listing payload
    const pickShippingDetailsCity = [
      pickupSel.city && pickupSel.countryCode ? `${pickupSel.city},${pickupSel.countryCode}` : null,
      shippingSel.city && shippingSel.countryCode ? `${shippingSel.city},${shippingSel.countryCode}` : null,
    ].filter(Boolean);

    const pickShippingDetailsCountry = [pickupSel.countryName, shippingSel.countryName].filter(Boolean);
    const pickShippingDetailsState = [pickupSel.selectedState, shippingSel.selectedState].filter(Boolean);
    const pickShippingDetailsZip = [pickupSel.zip, shippingSel.zip].filter(Boolean);

    const pickShippingGeoloc = [pickupSel.origin, shippingSel.origin]
      .filter(p => p && typeof p.lat === 'number' && typeof p.lng === 'number')
      .map(p => ({ lat: p.lat, lng: p.lng }));

    return {
      seller_id: userId || null,
      ...(pickShippingDetails.length ? { pickShippingDetails } : {}),
      pickShippingOptions,
      ...(pickShippingDetailsCity.length ? { pickShippingDetailsCity } : {}),
      ...(pickShippingDetailsCountry.length ? { pickShippingDetailsCountry } : {}),
      ...(pickShippingDetailsState.length ? { pickShippingDetailsState } : {}),
      ...(pickShippingDetailsZip.length ? { pickShippingDetailsZip } : {}),
      ...(pickShippingGeoloc.length ? { pickShippingGeoloc } : {}),
      ...(pickShippingGeoloc.length ? { _geoloc: pickShippingGeoloc } : {}),
    };
  } catch (e) {
    console.error('makeAlogoliaLocationData error', e);
    return { seller_id: userId || null };
  }
}



export const convertTo12Hour = (time24) => {
  // Validate input type
  if (typeof time24 !== 'string') {
    return '';
  }
  // Check pattern (HH:mm)
  const match = time24.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!match) {
    return '';
  }

  const [_, hoursStr, minutesStr] = match;
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);

  const suffix = hours >= 12 ? 'PM' : 'AM';
  const hours12 = ((hours + 11) % 12) + 1;

  return `${hours12}:${minutes.toString().padStart(2, '0')} ${suffix}`;
}

/**
 * Sort dropdown options alphabetically with special handling for "Other" and "Custom" options.
 * These special options are always placed at the bottom of the list.
 *
 * @param {Array} options - Array of option objects
 * @param {String} labelKey - The key to use for sorting (default: 'label')
 * @param {Array} bottomOptions - Array of values that should be anchored to the bottom (default: ['Other', 'Custom', 'Other - Fill in Below'])
 * @returns {Array} Sorted array with special options at the bottom
 */
export const sortOptionsAlphabetically = (options, labelKey = 'label', bottomOptions = ['Other', 'Custom', 'Other - Fill in Below']) => {
  if (!Array.isArray(options) || options.length === 0) {
    return options;
  }

  // Separate special options from regular options
  const specialOptions = [];
  const regularOptions = [];

  options.forEach(option => {
    const labelValue = option[labelKey];
    if (labelValue && bottomOptions.includes(labelValue)) {
      specialOptions.push(option);
    } else {
      regularOptions.push(option);
    }
  });

  // Sort regular options alphabetically
  regularOptions.sort((a, b) => {
    const aLabel = a[labelKey] || '';
    const bLabel = b[labelKey] || '';
    return aLabel.localeCompare(bLabel);
  });

  // Return regular options followed by special options
  return [...regularOptions, ...specialOptions];
};

export const getCloudinaryPdfThumbnail = url => {
  if (!url || !url.includes('/image/upload/')) return null;
  const lower = url.toLowerCase();
  if (!lower.includes('.pdf')) return null;
  return url.replace('/image/upload/', '/image/upload/w_46,h_62,c_fill,pg_1,f_jpg/');
};

export const getDocumentFileType = url => {
  if (!url) return null;
  const lower = url.toLowerCase();
  if (lower.includes('.pdf')) return 'PDF';
  if (lower.includes('.docx') || lower.includes('.doc')) return 'DOC';
  if (lower.includes('.xlsx') || lower.includes('.xls')) return 'XLS';
  if (lower.includes('.pptx') || lower.includes('.ppt')) return 'PPT';
  if (lower.includes('.csv')) return 'CSV';
  if (lower.includes('.txt')) return 'TXT';
  return 'FILE';
};


export const isUserSyncShopify = (user) => {
  const { profile } = user?.attributes || {};
  const { publicData } = profile || {};
  const { currentUserType } = publicData || {};
  const normalizedUserType = String(currentUserType || '').toLowerCase();

  return (
    currentUserType == CURRENT_USER_BUSINESS_TYPE ||
    normalizedUserType.includes('nonprofit') ||
    normalizedUserType.includes('non-profit')
  );
};
